/**
 * @fileoverview Centralized Logging Utility
 *
 * Provides structured logging to replace scattered console.log/error calls.
 * Features:
 * - Categorized logging (error, warn, info, debug)
 * - Environment-aware (respects NODE_ENV)
 * - Consistent formatting
 * - Future-ready for external logging services (Sentry, LogRocket, etc.)
 *
 * @author Engineering Team
 * @copyright Cirrus Invicta 2025
 * @private
 * @version 1.0.0
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug'

interface LogContext {
    [key: string]: unknown
}

/**
 * Centralized logger to replace direct console usage
 */
export class Logger {
    private static readonly isDevelopment = import.meta.env.DEV

    /**
     * Log error messages
     * @param message - Error message
     * @param context - Optional context data
     */
    static error(message: string, context?: LogContext): void {
        this.log('error', message, context)
    }

    /**
     * Log warning messages
     * @param message - Warning message
     * @param context - Optional context data
     */
    static warn(message: string, context?: LogContext): void {
        this.log('warn', message, context)
    }

    /**
     * Log informational messages
     * @param message - Info message
     * @param context - Optional context data
     */
    static info(message: string, context?: LogContext): void {
        this.log('info', message, context)
    }

    /**
     * Log debug messages (only in development)
     * @param message - Debug message
     * @param context - Optional context data
     */
    static debug(message: string, context?: LogContext): void {
        if (this.isDevelopment) {
            this.log('debug', message, context)
        }
    }

    /**
     * Core logging implementation
     * @private
     */
    private static log(level: LogLevel, message: string, context?: LogContext): void {
        const timestamp = new Date().toISOString()
        const prefix = `[${timestamp}] [${level.toUpperCase()}]`

        // Console output with appropriate method
        const consoleMethod = level === 'debug' ? console.log : console[level]

        if (context && Object.keys(context).length > 0) {
            consoleMethod(`${prefix} ${message}`, context)
        } else {
            consoleMethod(`${prefix} ${message}`)
        }

        // Future: Send to external logging service
        // this.sendToExternalService(level, message, context)
    }

    /**
     * Placeholder for future external logging integration
     * Future: Integrate with Sentry, LogRocket, or other service
     * @private
     */
    // private static sendToExternalService(
    //     level: LogLevel,
    //     message: string,
    //     context?: LogContext
    // ): void {
    //     // Example:
    //     // if (level === 'error') {
    //     //     Sentry.captureException(new Error(message), { extra: context })
    //     // }
    // }
}

/**
 * Convenience exports for cleaner imports
 */
export const logger = Logger
